import React, { useEffect, useState } from "react";
import { Box, Chip, Link, Typography } from "@mui/material";
import { FileDown } from "lucide-react";
import type { AssistantArtifactPart } from "../../../chat/types";
import { AnimatedCard } from "../../Common/AnimatedCard";
import { MarkdownView } from "../Markdown/MarkdownView";
import { downloadName, isMarkdownMime } from "./artifactFilename";
import { ArtifactExportMenu } from "./ArtifactExportMenu";
import { MARKDOWN_EXPORT_FORMATS } from "./artifactExportFormats";

const MAX_BLOB_BYTES = 5 * 1024 * 1024;  // 5 MB

/** A file artifact whose content is human-readable text (markdown, plain text,
 *  or a text/* mime) should render inline — document generation is a headline
 *  flow and a bare download chip hides the result. Binary files (pdf, images,
 *  octet-stream) stay download-only. */
function isInlineTextDoc(mime: string | null): boolean {
  if (!mime) return true; // no mime → generated text doc (markdown by convention)
  const m = mime.toLowerCase();
  return (
    m.startsWith("text/") ||
    m === "application/json" ||
    m === "application/xml" ||
    m.includes("markdown")
  );
}

export function ArtifactFileCard({ part }: { part: AssistantArtifactPart }) {
  const [href, setHref] = useState<string | null>(null);
  // Why the download link is unavailable, so the chip label is accurate:
  // "too-large" (over the embed cap) vs "unavailable" (createObjectURL failed).
  const [noLinkReason, setNoLinkReason] = useState<"too-large" | "unavailable" | null>(null);

  useEffect(() => {
    if (part.text.length > MAX_BLOB_BYTES) {
      // Too big to safely embed as a Blob URL; show without a download link.
      setHref(null);
      setNoLinkReason("too-large");
      return;
    }
    // Guard createObjectURL: it can throw (a strict CSP blocking blob:, quota,
    // or an environment without the API). An unguarded throw in this effect
    // unmounts the whole card and the artifact renders BLANK on reload — the
    // "reopen the chat and the artifact is not there" report. Degrade to the
    // no-download chip instead so the artifact is always visible.
    let url: string | null = null;
    try {
      const blob = new Blob([part.text], { type: part.mime ?? "application/octet-stream" });
      url = URL.createObjectURL(blob);
      setHref(url);
      setNoLinkReason(null);
    } catch {
      setHref(null);
      setNoLinkReason("unavailable");
      return;
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [part.text, part.mime]);

  const chip = href ? (
    <Chip
      data-ph="download"
      size="small"
      icon={<FileDown size={14} />}
      label={part.title ?? "file"}
      component={Link}
      href={href}
      download={downloadName(part.title, part.mime)}
      clickable
    />
  ) : (
    <Chip
      size="small"
      icon={<FileDown size={14} />}
      label={`${part.title ?? "file"}${
        noLinkReason === "too-large" ? " (too large to embed)" : " (download unavailable)"
      }`}
    />
  );

  const inline = isInlineTextDoc(part.mime);

  // Binary file (pdf/image/octet-stream): download-only, no inline body.
  if (!inline) {
    return (
      <Box sx={{ my: 1 }}>
        {chip}
        {part.status === "streaming" && (
          <Typography variant="caption" sx={{ color: "text.secondary", ml: 1 }}>
            streaming…
          </Typography>
        )}
      </Box>
    );
  }

  // Text document (markdown/text/json/…): render inline, with the title + a
  // compact download affordance in the header row.
  return (
    <AnimatedCard variant="sm" sx={{ my: 1, p: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        {chip}
        {part.status === "streaming" && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            streaming…
          </Typography>
        )}
        {part.status === "failed" && (
          <Typography variant="caption" color="error">
            failed{part.error ? `: ${part.error}` : ""}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <ArtifactExportMenu
          artifactId={part.artifact_id}
          title={part.title}
          formats={MARKDOWN_EXPORT_FORMATS}
          disabled={!(isMarkdownMime(part.mime) && part.status === "complete")}
        />
      </Box>
      <MarkdownView source={part.text} />
    </AnimatedCard>
  );
}
